import { Icon } from "@iconify/react";
import React from "react";
import { Link } from "react-router-dom";
import "../../pages/internal/YouWoConsumerLanding.css";
import ModernFooter from "../landing/modern/ModernFooter";
import SEOHead from "./SEOHead";
import ToolsLandingNav from "./ToolsLandingNav";
import "./ToolsLandingShell.css";

interface ComparisonPoint {
  feature: string;
  youwoai: string | boolean;
  competitor: string | boolean;
}

interface SEOLandingPageProps {
  title: string;
  metaDescription: string;
  keywords: string;
  canonicalUrl: string;
  headline: string;
  subheadline: string;
  heroIcon: string;
  heroIconColor: string;
  heroIconBg: string;
  problemTitle: string;
  problemDescription: string;
  solutionTitle: string;
  solutionDescription: string;
  competitorName?: string;
  comparisonPoints?: ComparisonPoint[];
  features: {
    icon: string;
    title: string;
    description: string;
  }[];
  ctaText: string;
  ctaLink: string;
  relatedTools: {
    name: string;
    href: string;
    icon: string;
  }[];
}

const renderComparisonValue = (value: string | boolean, muted = false) => {
  if (typeof value === "boolean") {
    return (
      <span className={`yw-seo-comparison-value ${value ? "" : "yw-seo-comparison-value-muted"}`}>
        <Icon icon={value ? "lucide:check" : "lucide:x"} />
        {value ? "Yes" : "No"}
      </span>
    );
  }

  return (
    <span className={`yw-seo-comparison-value ${muted ? "yw-seo-comparison-value-muted" : ""}`}>
      {value}
    </span>
  );
};

const SEOLandingPage: React.FC<SEOLandingPageProps> = ({
  title,
  metaDescription,
  keywords,
  canonicalUrl,
  headline,
  subheadline,
  heroIcon,
  heroIconColor,
  heroIconBg,
  problemTitle,
  problemDescription,
  solutionTitle,
  solutionDescription,
  competitorName,
  comparisonPoints,
  features,
  ctaText,
  ctaLink,
  relatedTools,
}) => {
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": title,
    "description": metaDescription,
    "url": canonicalUrl,
  };

  return (
    <>
      <SEOHead
        title={title}
        description={metaDescription}
        keywords={keywords}
        canonicalUrl={canonicalUrl}
        structuredData={structuredData}
      />

      <main className="yw-consumer-page yw-tools-page yw-tools-shell" data-lang="en" data-variant="tools">
        <ToolsLandingNav links={[
          { label: "Tools", href: "/tools" },
          { label: "Proof", href: "#proof" },
          { label: "Related", href: "#related" },
        ]} />

        <section className="yw-tools-hero">
          <div className="yw-tools-hero-copy">
            <p className="yw-tools-kicker">
              <span className={`yw-tools-hero-icon ${heroIconBg}`}>
                <Icon icon={heroIcon} className={heroIconColor} />
              </span>
              Source-backed AI tools
            </p>
            <h1>{headline}</h1>
            <p>{subheadline}</p>
            <div className="yw-tools-hero-actions">
              <a className="yw-tools-primary-link" href={ctaLink} target="_blank" rel="noopener noreferrer">
                {ctaText}
                <Icon icon="lucide:arrow-up-right" />
              </a>
              <Link className="yw-tools-secondary-link" to="/tools">
                Explore tools
              </Link>
            </div>
          </div>

          <aside className="yw-seo-hero-panel" aria-label="YouWo source workflow preview">
            <div className="yw-seo-hero-window">
              <div className="yw-seo-hero-source">
                <strong>Bring in the source</strong>
                <span>Lecture recording, PDF, notes, YouTube video, or pasted study material.</span>
              </div>
              <div className="yw-seo-hero-output">
                <strong>YouWo turns it into work</strong>
                <span>Answers stay connected to the material they came from.</span>
                <div>
                  <Icon icon="lucide:file-text" />
                  Summary with citations
                </div>
                <div>
                  <Icon icon="lucide:layers" />
                  Flashcards and quizzes
                </div>
                <div>
                  <Icon icon="lucide:message-circle" />
                  Follow-up chat
                </div>
              </div>
            </div>
          </aside>
        </section>

        <section className="yw-tools-section" id="proof">
          <div className="yw-tools-section-heading">
            <h2>From manual study prep to source-backed output.</h2>
          </div>

          <div className="yw-seo-proof-grid">
            <article className="yw-seo-proof-card">
              <Icon icon="lucide:x-circle" />
              <h3>{problemTitle}</h3>
              <p>{problemDescription}</p>
            </article>
            <article className="yw-seo-proof-card">
              <Icon icon="lucide:check-circle-2" />
              <h3>{solutionTitle}</h3>
              <p>{solutionDescription}</p>
            </article>
          </div>
        </section>

        {competitorName && comparisonPoints ? (
          <section className="yw-seo-comparison-section">
            <div className="yw-tools-section-heading">
              <h2>YouWo compared with {competitorName}</h2>
              <p>Keep the comparison focused on what students actually need: source inputs, AI generation, and reusable study outputs.</p>
            </div>

            <div className="yw-seo-comparison-card">
              <div className="yw-seo-comparison-head">
                <div>Feature</div>
                <div>YouWo</div>
                <div>{competitorName}</div>
              </div>
              {comparisonPoints.map((point) => (
                <div className="yw-seo-comparison-row" key={point.feature}>
                  <strong>{point.feature}</strong>
                  <div>{renderComparisonValue(point.youwoai)}</div>
                  <div>{renderComparisonValue(point.competitor, true)}</div>
                </div>
              ))}
            </div>
          </section>
        ) : null}

        <section className="yw-tools-section">
          <div className="yw-tools-section-heading">
            <h2>What makes YouWo different?</h2>
            <p>YouWo is built for the full loop: capture the source, understand it, then create the next study artifact.</p>
          </div>

          <div className="yw-tools-feature-grid">
            {features.map((feature) => (
              <article className="yw-tools-card" key={feature.title}>
                <Icon icon={feature.icon} className="yw-tools-card-icon" />
                <h3>{feature.title}</h3>
                <p>{feature.description}</p>
              </article>
            ))}
          </div>
        </section>

        <section className="yw-tools-final-cta">
          <div>
            <h2>Start from the source. Let YouWo make the next thing.</h2>
            <p>Open YouWo to summarize, transcribe, generate flashcards, build quizzes, and ask follow-up questions from the same material.</p>
            <a href={ctaLink} target="_blank" rel="noopener noreferrer">
              {ctaText}
              <Icon icon="lucide:arrow-up-right" />
            </a>
          </div>
        </section>

        <section className="yw-tools-section" id="related">
          <div className="yw-tools-section-heading">
            <h2>Related tools</h2>
            <p>Move between study workflows without losing the source context.</p>
          </div>

          <div className="yw-tools-related-grid">
            {relatedTools.map((tool) => (
              <Link className="yw-tools-related-card" key={tool.href} to={tool.href}>
                <Icon icon={tool.icon} className="yw-tools-card-icon" />
                <h3>{tool.name}</h3>
                <span>
                  Open tool
                  <Icon icon="lucide:arrow-right" />
                </span>
              </Link>
            ))}
          </div>
        </section>
      </main>

      <div className="yw-tools-footer-shell">
        <ModernFooter />
      </div>
    </>
  );
};

export default SEOLandingPage;
